import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModalTitleFileNameAndSize from 'Components/ModalTitleFileNameAndSize';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useAuthenticationStore } from 'Hooks/zustand/useAuthenticationStore';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import {
    POSSIBLE_STATUS,
    updateStatus,
    useUploaderContext,
    useUploaderDispatchContext
} from '../../components/Uploader/context';
import styles from './useBulkListServerError.css';

function useBulkListServerError() {
    const { currentChunkNumber, fileDetails, chunkSize } = useUploaderContext();
    const dispatch = useUploaderDispatchContext();
    const { clearAll, addModal } = useReduxModal();
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();

    const cb = React.useCallback(() => {
        clearAll();
        dispatch(updateStatus(POSSIBLE_STATUS.error));
        addModal({
            id: 'bulk-uploader-error',
            status: 'error',
            title: 'Bulk Upload Error',
            onClose: async () => {
                /**
                 * @note Refreshing the access token here is temp a solution.
                 *
                 * Since PAN search, field and uploader renders an iframe that
                 * is uses the access token in the payload, refreshing
                 * the access token inside the iframe is not working for now.
                 * To make sure the user will have at least 10 minutes
                 * before the access token expire, refreshing the tokens
                 * happen when the user reloading the page.
                 *
                 * Impacted components:
                 * - BcatsMaskedField
                 * - PCISearch
                 * - PCIFileUploader
                 * - PCIInput
                 */
                const { authService } = useAuthenticationStore.getState();
                await authService?.refreshAccessToken();
                useAccessTokenStore.setState({ accessToken: await authService?.getAccessToken() });

                window.location.reload();
            },
            cancelText: t('Reload page'),
            content: (
                <>
                    {t('singleListBulkUpload.error')}
                    <ul className={styles.addCount}>
                        <li>
                            {t('singleListBulkUpload.successfullyUpdated', {
                                count: currentChunkNumber * chunkSize
                            })}
                        </li>
                    </ul>
                    {t('singleListBulkUpload.closeThisWindow')}
                </>
            ),
            titleChildren: (
                <ModalTitleFileNameAndSize
                    containerClassName={styles.errorTitle}
                    fileName={fileDetails.fileName}
                    fileSize={fileDetails.fileSize}
                />
            )
        });
    }, [
        addModal,
        chunkSize,
        clearAll,
        currentChunkNumber,
        dispatch,
        fileDetails.fileName,
        fileDetails.fileSize,
        history,
        location.pathname,
        t
    ]);

    return cb;
}

export default useBulkListServerError;
